import { Alert, Box, Stack, Typography } from "@mui/material";

import { formatSaoPauloDateTime } from "../dates";
import { IRondaFinding, IRondaStatus } from "../contracts/ronda.types";
import { HOME_TOKENS } from "./tokens";

const RONDA_STALE_HOURS = 26;

/** Fonte do `ronda.ps1`, padrão de quem não diz a fonte (backend anterior à #2255). */
const SOURCE_INVARIANTES = "invariantes";
const SOURCE_CAMPANHAS = "campanhas";

const SOURCE_LABELS: Record<string, string> = { [SOURCE_CAMPANHAS]: "Ronda de campanhas" };

const sourceOf = (f: IRondaFinding) => f.source ?? SOURCE_INVARIANTES;

function FindingLine({ finding, current }: { finding: IRondaFinding; current: boolean }) {
  return (
    <Typography
      variant="body2"
      color={!current ? "text.secondary" : finding.invariant === "COBERTURA" ? "warning.main" : "error.main"}
    >
      [{finding.cnpj ?? finding.conexao}] {finding.invariant}: {finding.detalhe}
    </Typography>
  );
}

const findingKey = (f: IRondaFinding) =>
  `${sourceOf(f)}::${f.invariant}::${f.conexao}::${f.workspaceId ?? ""}::${f.detalhe}`;

/**
 * Uma fonte da Ronda além do `ronda.ps1` (hoje, a Ronda de campanhas da F4). Seção própria
 * para os CT-* não aparecerem como "Invariantes da plataforma", com o frescor da própria
 * fonte: o heartbeat do `ronda.ps1` não diz nada sobre ela.
 *
 * invariant: achado de fonte sem rodada recente nunca é pintado como atual. Sem rodada
 * nenhuma é "sem evidência", nunca "falhou" nem "sem achados".
 */
function FonteSection({
  source,
  lastRunAt,
  findings,
  isError,
}: {
  source: string;
  lastRunAt: string | null;
  findings: IRondaFinding[];
  /** Leitura falhou e o que se vê é cache: nada aqui é atual, seja qual for o carimbo. */
  isError: boolean;
}) {
  const label = SOURCE_LABELS[source] ?? `Ronda "${source}"`;
  const current = !isError && !isRondaStale(lastRunAt);
  const caveat = findings.length > 0 ? " Os achados abaixo podem não valer mais." : "";

  return (
    <Box component="section" aria-label={label} sx={{ mt: `${HOME_TOKENS.space.withinRow * 2}px` }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        color={current ? "text.secondary" : "warning.main"}
        sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}
      >
        {isError && lastRunAt
          ? `${label}: não foi possível ler o estado agora; última rodada conhecida em ${formatSaoPauloDateTime(lastRunAt)}.${caveat}`
          : !lastRunAt
            ? `${label}: sem evidência de rodada.${caveat}`
            : !current
              ? `${label} sem rodada desde ${formatSaoPauloDateTime(lastRunAt)}.${caveat}`
              : findings.length === 0
                ? `${label}: última rodada em ${formatSaoPauloDateTime(lastRunAt)}, sem achados abertos.`
                : `${label}: última rodada em ${formatSaoPauloDateTime(lastRunAt)}.`}
      </Typography>
      <Stack spacing={0.5}>
        {findings.map((f) => (
          <FindingLine key={findingKey(f)} finding={f} current={current} />
        ))}
      </Stack>
    </Box>
  );
}

/** Buffer de 2h sobre a janela diária de 24h — mesma folga que `integracao_painel.md` já define ("última ronda > 26h atrás"). */
function isRondaStale(lastRunAt: string | null): boolean {
  if (!lastRunAt) return true;
  const elapsedMs = Date.now() - new Date(lastRunAt).getTime();
  return elapsedMs > RONDA_STALE_HOURS * 60 * 60 * 1000;
}

/**
 * Seção "Invariantes da plataforma" — Ronda de Invariantes
 * (`04_agent_operations/ronda_invariantes/`, Drive), varredura diária de
 * Snowflake fora do Airflow: tasks suspensas, derivadas estagnadas, decisões
 * sem carimbo. Nasceu do incidente de 2026-09-04 (task de transferência
 * suspensa em silêncio por 7 semanas, zero sinal).
 *
 * Deliberadamente FORA do union `CnpjState` que `panel-home.ts` compõe: essa
 * precedência (CONDUCCO/EM_DIA/PARADO) já levou várias rodadas de correção
 * fina (ver os comentários "Blocker" no backend), e fundir a Ronda ali
 * reabriria esse risco sem necessidade — uma seção própria é aditiva de
 * verdade, sem tocar a lógica existente.
 *
 * "Quem vigia o vigia" (`integracao_painel.md`): se a própria ronda não
 * rodou nas últimas 26h, OU se não conseguimos ler o estado dela, a seção
 * avisa disso em vez de ficar em silêncio — a ausência da ronda é, ela
 * mesma, um sinal que não pode desaparecer.
 *
 * invariant: a cobertura aparece SEMPRE, inclusive quando não há exceção
 * nenhuma. "Sem exceções" sem denominador é a mesma cegueira de outra forma:
 * a ronda visita uma lista fixa de contas e o painel lista o portfólio
 * inteiro, então verde só quer dizer "nas que eu olhei".
 */
export function RondaSection({
  status,
  isError = false,
  cnpjsNoPainel = [],
}: {
  status: IRondaStatus | undefined;
  isError?: boolean;
  /** `federalTaxId` de cada CNPJ que o painel lista — o denominador. */
  cnpjsNoPainel?: string[];
}) {
  // why: sem status e sem erro a query ainda está em voo. A seção só aparece
  // quando há algo verdadeiro a dizer.
  if (!status && !isError) return null;

  // hazard: falha de leitura também é ausência de sinal. Antes o componente
  // devolvia null neste caso e a seção sumia da tela sem dizer nada, que é a
  // mesma cegueira que a ronda existe para eliminar: o operador via uma
  // página sem alarme e concluía que estava tudo certo.
  const stale = isError || !status || isRondaStale(status.lastRunAt);

  const totalNoPainel = cnpjsNoPainel.length;
  const verificados = status?.coverage
    ? cnpjsNoPainel.filter((cnpj) => status.coverage?.scannedCnpjs.includes(cnpj)).length
    : 0;
  const coberturaParcial =
    totalNoPainel === 0 || !status?.coverage || verificados < totalNoPainel;

  // why: desde a #2255 cada achado diz a fonte e `lastRunAt`/`coverage` são só do ronda.ps1.
  // Backend antigo não manda `source` nem `fontes`: tudo é invariante, e a tela fica como era.
  const allFindings = status?.findings ?? [];
  const invariantes = allFindings.filter((f) => sourceOf(f) === SOURCE_INVARIANTES);
  const outrasFontes = status?.fontes
    ? Array.from(
        new Set([
          // invariant: com backend novo, a Ronda de campanhas aparece sempre, mesmo sem rodada
          // nenhuma: a ausência dela é sinal, como a do ronda.ps1.
          SOURCE_CAMPANHAS,
          ...Object.keys(status.fontes),
          ...allFindings.map(sourceOf).filter((s) => s !== SOURCE_INVARIANTES),
        ]),
      )
    : [];

  return (
    <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
      <Box component="section" aria-label="Invariantes da plataforma">
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}
        >
          Invariantes da plataforma
        </Typography>

        {stale && (
          <Alert severity="warning" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
            {isError || !status
              ? status?.lastRunAt
                ? // why: mesmo padrao que a review v2.1 fixou para a lista
                  // principal desta pagina. O alerta sozinho nao basta: sem dizer
                  // a idade, o operador le a lista em cache como estado atual.
                  `Não foi possível ler o estado da ronda. O que aparece abaixo é o dado de ${formatSaoPauloDateTime(status.lastRunAt)} e pode estar desatualizado; nunca leia como "tudo certo".`
                : "Não foi possível ler o estado da ronda."
              : status.lastRunAt
                ? `Ronda não rodou hoje — última execução em ${formatSaoPauloDateTime(status.lastRunAt)}.`
                : "Ronda ainda não rodou nenhuma vez."}
          </Alert>
        )}

        {/* invariant: a cobertura aparece SEMPRE que ha status. Zero e o valor
            mais informativo de todos, e era o unico que a guarda antiga apagava:
            com `totalNoPainel > 0` no gate, um painel sem CNPJs (que acontece
            quando o /home falha e devolve `cnpjs: []` com sourcesAvailable
            false) deixava na tela so "sem exceções", sem denominador nenhum.
            Verde sem universo e exatamente a afirmacao que esta razao existe
            para impedir. A frase muda de texto, nunca de existencia. */}
        {status && (
          <Typography
            variant="body2"
            color={coberturaParcial ? "warning.main" : "text.secondary"}
            sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}
          >
            {totalNoPainel === 0
              ? "Cobertura desconhecida: o painel não conseguiu listar os CNPJs desta carteira."
              : status.coverage
                ? `Cobertura: ${verificados} de ${totalNoPainel} CNPJs do painel verificados nesta rodada.`
                : `Cobertura desconhecida: a rodada não informou quais dos ${totalNoPainel} CNPJs foram verificados.`}
          </Typography>
        )}

        {/* why: o React Query preserva `data` quando a query erra. Achado ja
            observado continua sendo achado real, entao o alerta e a lista
            aparecem JUNTOS, mesmo padrao que a review v2.1 de 20/08 fixou para
            a lista principal desta pagina: esconder falha real atras de um
            banner foi exatamente o bloqueador corrigido la. */}
        {status &&
          (invariantes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {/* why: dizer "Ronda de hoje" ao lado do alerta "Ronda nao rodou
                  hoje" punha as duas afirmacoes contraditorias na mesma tela, e
                  quem le de baixo para cima via o verde. "Ultima ronda" e
                  verdade nos dois estados. */}
              {status.lastRunAt
                ? `Última ronda (${formatSaoPauloDateTime(status.lastRunAt)}): sem exceções nas contas verificadas.`
                : "Sem dado de ronda ainda."}
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {invariantes.map((f) => (
                <FindingLine key={findingKey(f)} finding={f} current />
              ))}
            </Stack>
          ))}
      </Box>

      {outrasFontes.map((source) => (
        <FonteSection
          key={source}
          source={source}
          lastRunAt={status?.fontes?.[source]?.lastRunAt ?? null}
          findings={allFindings.filter((f) => sourceOf(f) === source)}
          isError={isError}
        />
      ))}
    </Box>
  );
}
